import React from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { ManagerSelector } from '@/components/ManagerSelector';
import AddressAutocomplete from '@/components/common/AddressAutocomplete';
import { flashAutofilled } from '@/lib/flashAutofilled';
import { US_STATES, formatPhoneInput } from '@/lib/utils';
import { isKRRole, isAccountingRole } from '@/lib/utils';
import { toast } from 'sonner';
import { agentAPI } from '@/lib/api';
import { Shield, MessageSquare } from 'lucide-react';

/**
 * EditAgentDialog — owns its own form state, resets on open, submits via
 * agentAPI.update. Parent only needs to fire `onSaved` to refresh the profile.
 */
export const EditAgentDialog = ({ open, onOpenChange, agent, user, onSaved }) => {
  const [form, setForm] = React.useState({});
  const [saving, setSaving] = React.useState(false);

  // Seed the form whenever the dialog opens with a fresh agent record.
  React.useEffect(() => {
    if (open && agent) {
      setForm({
        first_name: agent.first_name || '',
        last_name: agent.last_name || '',
        email: agent.email || '',
        phone: agent.phone || '',
        date_of_birth: agent.date_of_birth || '',
        ffm_user_id: agent.ffm_user_id || '',
        ssn_full: '',
        agent_status: agent.agent_status || 'onboarding',
        address_line1: agent.address_line1 || '',
        address_line2: agent.address_line2 || '',
        city: agent.city || '',
        state: agent.state || '',
        zip: agent.zip || '',
        manager_id: agent.manager_id || '',
        manager_name: agent.manager_name || '',
        // Phase 27.37.N — Lead SMS permission (KR-only toggle).
        can_sms_leads: !!agent.can_sms_leads,
        // Preferred language (en|es) for bilingual SMS/email.
        preferred_language: (agent.preferred_language || 'en').toLowerCase().slice(0, 2),
      });
    }
  }, [open, agent]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      // Only send `ssn_full` when the operator actually typed a new value —
      // avoids overwriting the encrypted store with an empty string.
      const payload = { ...form };
      if (!payload.ssn_full) delete payload.ssn_full;
      // Phase 27.37.N — Only KR admins may toggle the Lead SMS
      // permission. Strip it for everyone else so we don't send a
      // no-op field (backend also enforces this defense-in-depth).
      if (!isKRRole(user)) delete payload.can_sms_leads;
      await agentAPI.update(agent.npn, payload);
      toast.success('Agent updated');
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to update agent');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading">Edit Agent</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>First Name *</Label>
                <Input value={form.first_name || ''} onChange={(e) => setForm((p) => ({ ...p, first_name: e.target.value }))} required />
              </div>
              <div className="space-y-2">
                <Label>Last Name *</Label>
                <Input value={form.last_name || ''} onChange={(e) => setForm((p) => ({ ...p, last_name: e.target.value }))} required />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Email</Label>
                <Input type="email" value={form.email || ''} onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))} />
              </div>
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input value={formatPhoneInput(form.phone || '')} onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="flex items-center gap-2"><MessageSquare size={13} className="text-sky-600" /> Preferred Language</Label>
                <Select value={form.preferred_language || 'en'} onValueChange={(v) => setForm((p) => ({ ...p, preferred_language: v }))}>
                  <SelectTrigger data-testid="edit-preferred-language"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="en">English</SelectItem>
                    <SelectItem value="es">Español</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-[10px] text-slate-500">Texts &amp; emails (reminders, confirmations, urgent tasks) use this language.</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Date of Birth</Label>
                <Input type="date" value={form.date_of_birth || ''} onChange={(e) => setForm((p) => ({ ...p, date_of_birth: e.target.value }))} data-testid="edit-dob" />
              </div>
              <div className="space-y-2">
                <Label>FFM User ID</Label>
                <Input value={form.ffm_user_id || ''} onChange={(e) => setForm((p) => ({ ...p, ffm_user_id: e.target.value }))} placeholder="FFM username" data-testid="edit-ffm-user" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="flex items-center gap-2">Social Security <Shield size={13} className="text-amber-600" /></Label>
                <Input
                  value={form.ssn_full || ''}
                  onChange={(e) => {
                    let v = e.target.value.replace(/\D/g, '').slice(0, 9);
                    if (v.length > 5) v = `${v.slice(0, 3)}-${v.slice(3, 5)}-${v.slice(5)}`;
                    else if (v.length > 3) v = `${v.slice(0, 3)}-${v.slice(3)}`;
                    setForm((p) => ({ ...p, ssn_full: v }));
                  }}
                  maxLength={11}
                  placeholder="XXX-XX-XXXX"
                  data-testid="edit-ssn"
                />
                <p className="text-[10px] text-amber-600">Encrypted and stored securely. Last 4 auto-extracted.</p>
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={form.agent_status || 'onboarding'} onValueChange={(v) => setForm((p) => ({ ...p, agent_status: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                    <SelectItem value="onboarding">Onboarding</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Separator />
            {(isKRRole(user) || isAccountingRole(user)) && (
              <div className="space-y-2">
                <Label>Manager / Development Leader <span className="text-xs text-slate-500 ml-1">(optional)</span></Label>
                <ManagerSelector
                  value={form.manager_id || ''}
                  valueName={form.manager_name}
                  onChange={(id, name) => setForm((p) => ({ ...p, manager_id: id || '', manager_name: name || '' }))}
                  testId="edit-manager-selector"
                />
              </div>
            )}
            {/* Phase 27.37.N — Lead SMS permission (KR admin only). */}
            {isKRRole(user) && (
              <div
                className="rounded-lg border border-slate-200 bg-slate-50 p-3 flex items-start justify-between gap-3"
                data-testid="edit-can-sms-leads-row"
              >
                <div className="flex items-start gap-2">
                  <MessageSquare size={16} className="text-sky-600 mt-0.5" />
                  <div>
                    <Label className="text-sm font-semibold">Puede enviar SMS a Leads</Label>
                    <p className="text-xs text-slate-500 mt-0.5">
                      Cuando está desactivado, este agente no puede enviar mensajes
                      de texto a Leads desde la app. Los KR Admin siempre pueden
                      enviar sin importar este permiso.
                    </p>
                  </div>
                </div>
                <Switch
                  checked={!!form.can_sms_leads}
                  onCheckedChange={(v) => setForm((p) => ({ ...p, can_sms_leads: !!v }))}
                  data-testid="edit-can-sms-leads-toggle"
                />
              </div>
            )}
            <div className="space-y-2">
              <Label>Address Line 1</Label>
              <AddressAutocomplete
                value={form.address_line1 || ''}
                onChange={(v) => setForm((p) => ({ ...p, address_line1: v }))}
                onPlaceSelected={(parsed) => {
                  setForm((p) => ({
                    ...p,
                    address_line1: parsed.street || parsed.formatted,
                    city: parsed.city || p.city,
                    state: parsed.state || p.state,
                    zip: parsed.zip || p.zip,
                  }));
                  flashAutofilled(['edit-agent-city', 'edit-agent-state', 'edit-agent-zip']);
                }}
                testId="edit-agent-address1"
              />
            </div>
            <div className="space-y-2"><Label>Address Line 2</Label><Input value={form.address_line2 || ''} onChange={(e) => setForm((p) => ({ ...p, address_line2: e.target.value }))} /></div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2"><Label>City</Label><Input value={form.city || ''} onChange={(e) => setForm((p) => ({ ...p, city: e.target.value }))} data-testid="edit-agent-city" /></div>
              <div className="space-y-2">
                <Label>State</Label>
                <Select value={form.state || '__none__'} onValueChange={(v) => setForm((p) => ({ ...p, state: v === '__none__' ? '' : v }))}>
                  <SelectTrigger data-testid="edit-agent-state"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Select State</SelectItem>
                    {US_STATES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2"><Label>ZIP</Label><Input value={form.zip || ''} onChange={(e) => setForm((p) => ({ ...p, zip: e.target.value }))} data-testid="edit-agent-zip" /></div>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
            <Button type="submit" disabled={saving}>{saving ? 'Saving…' : 'Save Changes'}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EditAgentDialog;
